// src/app/services/ai-sandbox-service.ts
import { apiFetch } from "@/api/api-fetch";
import {
  normalizeProviderKey,
  MEASUREMENT_MODELS,
  getModelDisplayName,
} from "@/app/constants/models";

export type ActionReference = {
  label: string;
  url?: string | null;
  excerpt: string;
};

export type BeforeAfterData = {
  before: { avgProbability: number; topRank: number; brandMentionRate: number };
  after:  { avgProbability: number; topRank: number; brandMentionRate: number };
  delta:  { probability: number; rank: number; mentionRate: number };
  confidenceInterval?: { lower: number; upper: number };
  rankCI?: { lower: number; upper: number };
  monteCarlo?: { p5: number; p25: number; p50: number; p75: number; p95: number };
  effectSize?: "negligible" | "small" | "medium" | "large";
  statisticalSignificance?: "low" | "moderate" | "high";
};

export type ModelResult = {
  id: string;
  name: string;
  before: number;
  after: number;
  delta: number;
  /** No longer returned (#132): it was the model grading itself. */
  confidence?: number;
  responseTime: string;
  beforeRank: number;
  afterRank: number;
  ciLower?: number;
  ciUpper?: number;
  rankCILower?: number;
  rankCIUpper?: number;
  effectSize?: "negligible" | "small" | "medium" | "large";
  statisticalSignificance?: "low" | "moderate" | "high";
  monteCarlo?: { p5: number; p25: number; p50: number; p75: number; p95: number };
};

export type CompetitorResult = {
  name: string;
  before_probability: number;
  before_rank: number;
  after_brand_probability: number;
  after_brand_rank: number;
  competitor_probability: number;
  competitor_rank: number;
  gap_before: number;
  gap_after: number;
  is_closable: boolean;
};

export type TimeToImpact = {
  platform: string;
  model_family: string;
  estimated_days_min: number;
  estimated_days_max: number;
  /** No longer returned (#132): it was the model grading itself. */
  confidence_level?: "low" | "medium" | "high";
  where_to_update: string[];
  update_action: string;
  framework_or_site: string;
  why: string;
};

/**
 * Canonical before data shape the backend service expects.
 * overall = aggregate across all models
 * perModel = per-model breakdown keyed by normalised model id (openai / claude / perplexity)
 */
export type MonitoringBeforeData = {
  overall: { probability: number; rank: number };
  perModel: Record<string, { probability: number; rank: number }>;
};

export type SimulationQueueItem = {
  id: number;
  title: string;
  type: string;
  source: string;
  confidence: number;
  expectedImpact: string;
  summary: string;
  lastModified: string;
  strategicRationale?: { issues?: string[]; gaps?: string[]; opportunities?: string[] };
  priority?: string;
  description?: string;
  impact?: string | number;
  effort?: string;
  sourceIssue?: string;
  whereToUpdate?: string[];
  updateDetails?: string[];
  references?: ActionReference[];
};

export type AIModel = {
  id: string;
  name: string;
  enabled: boolean;
  avgResponseTime: string;
};

export type SimulationHistoryItem = {
  id: number;
  query: string;
  timestamp: string;
  models: string[];
  uploadedFiles: Array<{ filename: string; contentType: string; blobUrl: string }>;
  avgDelta: number;
  status: string;
  monitoringBeforeUsed?: boolean;
};

export type SimulationRunDetails = {
  runId: number;
  createdAt: string;
  query: string;
  beforeAfterData: BeforeAfterData;
  modelResults: ModelResult[];
  discoveries?: Array<{ title: string; description: string; tag: "positive" | "neutral" | "warning" }>;
  rationale?: { issues: string[]; gaps: string[]; opportunities: string[] };
  competitors?: CompetitorResult[];
  timeToImpact?: TimeToImpact[];
  monitoringBeforeUsed?: boolean;
  selectedQueueItems: SimulationQueueItem[];
  uploadedFiles: Array<{ filename: string; contentType: string; blobUrl: string }>;
  selectedModels: string[];
  status: string;
};

export type SimulationRunResponse = {
  runId: number;
  createdAt: string;
  beforeAfterData: BeforeAfterData;
  modelResults: ModelResult[];
  discoveries?: Array<{ title: string; description: string; tag: "positive" | "neutral" | "warning" }>;
  rationale?: { issues: string[]; gaps: string[]; opportunities: string[] };
  competitors?: CompetitorResult[];
  timeToImpact?: TimeToImpact[];
  monitoringBeforeUsed?: boolean;
  selectedQueueItems: SimulationQueueItem[];
  uploadedFiles: Array<{ filename: string; contentType: string; blobUrl: string }>;
  selectedModels: string[];
};

export type AISandboxData = {
  simulationQueue: SimulationQueueItem[];
  beforeAfterData: BeforeAfterData;
  modelResults: ModelResult[];
  aiModels: AIModel[];
  simulationHistory: SimulationHistoryItem[];
  comparisonData: any[];
  timeSeriesData: any[];
  responseQuality: any[];
  discoveries?: Array<{ title: string; description: string; tag: "positive" | "neutral" | "warning" }>;
  competitors?: CompetitorResult[];
  timeToImpact?: TimeToImpact[];
  rationale?: { issues: string[]; gaps: string[]; opportunities: string[] };
  monitoringBeforeUsed?: boolean;
  /** #132: the figures are model estimates with no band, not measurements. */
  estimateBasis?: "model_estimate";
  estimateNote?: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function _safeNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// extractMonitoringData
//
// Accepts the raw `antennaData` object (BrandMonitoringResponse) passed
// directly from App.tsx, OR the legacy bm_last_result from localStorage.
//
// Handles the following shapes from BrandMonitoringResponse:
//
//   PRIMARY SHAPE (antennaData from Antenna run):
//     {
//       modelComparison: [{ model: "Openai", score: 72.3, rank: 3 }, ...],
//       perceptionData:  { overallScore: 75.5 },
//       competitiveScopeRanks: { globalRank: 3, localRank: 2 },
//     }
//
//   LEGACY SHAPES (bm_last_result from localStorage):
//     Shape A: { modelResults:    [{ id: "openai", probability: 72, rank: 3 }] }
//     Shape B: { perModelResults: { openai: { probability: 72, rank: 3 } } }
//     Shape C: { perceptionScore: 75, topRank: 3 }
//
// Returns null only if absolutely nothing usable is found.
// ---------------------------------------------------------------------------

export function extractMonitoringData(
  raw: Record<string, any> | null | undefined
): MonitoringBeforeData | null {
  if (!raw || typeof raw !== "object") return null;

  const perModel: Record<string, { probability: number; rank: number }> = {};

  function register(modelId: string, prob: number | null, rank: number | null) {
    const mid = normalizeProviderKey(modelId);
    if (!mid || prob === null) return;
    // Don't overwrite a higher-quality entry
    if (!perModel[mid]) {
      perModel[mid] = { probability: prob, rank: rank ?? 10 };
    }
  }

  // ── Shape PRIMARY: modelComparison (BrandMonitoringResponse) ────────────
  //    Each item: { model: "Openai", score: 72.3, rank: 3, ... }
  const modelComparison = raw.modelComparison || [];
  if (Array.isArray(modelComparison)) {
    for (const m of modelComparison) {
      if (!m || typeof m !== "object") continue;
      // "model" field — e.g. "Openai", "Claude", "Perplexity"
      const id = String(m.model || m.id || "");
      if (!id) continue;
      const prob = _safeNum(m.score ?? m.probability ?? m.perceptionScore);
      const rank = _safeNum(m.rank ?? m.position);
      register(id, prob, rank != null ? Math.round(rank) : null);
    }
  }

  // ── Shape A: modelResults array ─────────────────────────────────────────
  //    [{ id: "openai", probability: 72, rank: 3 }]
  function processModelResultsArray(arr: any[]) {
    if (!Array.isArray(arr)) return;
    for (const m of arr) {
      if (!m || typeof m !== "object") continue;
      const id = String(m.id || m.model || "");
      if (!id) continue;
      const prob = _safeNum(m.probability ?? m.score ?? m.perceptionScore ?? m.recommendationRate);
      const rank = _safeNum(m.rank ?? m.position ?? m.averageRank);
      register(id, prob, rank != null ? Math.round(rank) : null);
    }
  }

  processModelResultsArray(raw.modelResults || []);

  // Look inside aggregatedData if present (some shapes nest it)
  const agg = raw.aggregatedData || raw.aggregated_data || {};
  if (agg && typeof agg === "object") {
    processModelResultsArray(agg.modelResults || agg.model_results || []);
    if (Array.isArray(agg.modelComparison)) {
      for (const m of agg.modelComparison) {
        if (!m || typeof m !== "object") continue;
        const id = String(m.model || m.id || "");
        const prob = _safeNum(m.score ?? m.probability);
        const rank = _safeNum(m.rank ?? m.position);
        register(id, prob, rank != null ? Math.round(rank) : null);
      }
    }
  }

  // ── Shape B: perModelResults / per_model_results dict ───────────────────
  const pmr = raw.perModelResults || raw.per_model_results
    || agg.perModelResults || agg.per_model_results || {};
  if (pmr && typeof pmr === "object") {
    for (const [key, vals] of Object.entries(pmr)) {
      if (!vals || typeof vals !== "object") continue;
      const v = vals as any;
      const prob = _safeNum(v.probability ?? v.score ?? v.perceptionScore ?? v.recommendationRate);
      const rank = _safeNum(v.rank ?? v.position ?? v.averageRank);
      register(key, prob, rank != null ? Math.round(rank) : null);
    }
  }

  // ── Overall probability ──────────────────────────────────────────────────
  // PRIMARY: perceptionData.overallScore (BrandMonitoringResponse)
  const pdScore = _safeNum(
    raw.perceptionData?.overallScore
    ?? raw.perceptionData?.score
  );

  // LEGACY: top-level fields
  const legacyProb = _safeNum(
    raw.overallProbability ?? raw.avgProbability
    ?? raw.perceptionScore ?? agg.overallProbability
    ?? agg.avgProbability ?? agg.perceptionScore
  );

  const overallProb = pdScore ?? legacyProb;

  // ── Overall rank ─────────────────────────────────────────────────────────
  // PRIMARY: competitiveScopeRanks.globalRank (most reliable for sandbox)
  //          fall back to localRank if global is 0
  const scopeRanks = raw.competitiveScopeRanks || {};
  const scopeGlobal = _safeNum(scopeRanks.globalRank);
  const scopeLocal  = _safeNum(scopeRanks.localRank);
  const scopeRank   = (scopeGlobal && scopeGlobal > 0) ? scopeGlobal
                    : (scopeLocal  && scopeLocal  > 0) ? scopeLocal
                    : null;

  // LEGACY: top-level rank fields
  const legacyRank = _safeNum(
    raw.overallRank ?? raw.topRank ?? raw.rank
    ?? agg.overallRank ?? agg.topRank ?? agg.rank
  );

  const overallRank = scopeRank ?? legacyRank;

  // ── Derive overall from per-model if still missing ───────────────────────
  const modelEntries = Object.values(perModel);
  const derivedOverallProb = modelEntries.length > 0
    ? Math.round(
        modelEntries.reduce((s, m) => s + m.probability, 0) / modelEntries.length * 10
      ) / 10
    : null;
  const derivedOverallRank = modelEntries.length > 0
    ? Math.min(...modelEntries.map((m) => m.rank))
    : null;

  const finalProb = overallProb ?? derivedOverallProb;
  const finalRank = overallRank != null ? Math.round(overallRank) : derivedOverallRank;

  // Nothing found at all
  if (finalProb === null && modelEntries.length === 0) return null;

  return {
    overall: {
      probability: finalProb ?? 0,
      rank: finalRank ?? 10,
    },
    perModel,
  };
}

/** Convert MonitoringBeforeData to the wire format the backend expects (snake_case). */
function monitoringDataToWire(md: MonitoringBeforeData): Record<string, any> {
  return {
    overall: { probability: md.overall.probability, rank: md.overall.rank },
    per_model: Object.fromEntries(
      Object.entries(md.perModel).map(([k, v]) => [
        k, { probability: v.probability, rank: v.rank },
      ])
    ),
  };
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function runSandboxSimulation(payload: {
  brand: string;
  query: string;
  models: string[];
  selectedQueueItems: SimulationQueueItem[];
  file?: File;
  competitors?: string[];
  monitoringData?: MonitoringBeforeData | null;
}): Promise<SimulationRunResponse> {
  const form = new FormData();
  form.append("brand", payload.brand);
  form.append("query", payload.query);
  form.append("models", JSON.stringify(payload.models));
  form.append("competitors", JSON.stringify(payload.competitors || []));
  form.append(
    "monitoringData",
    JSON.stringify(payload.monitoringData ? monitoringDataToWire(payload.monitoringData) : {})
  );

  const cleanedItems = (payload.selectedQueueItems || []).map((item) => ({
    id: item.id,
    priority: item.priority,
    title: item.title,
    summary: item.summary ?? item.description ?? "",
    description: item.description ?? item.summary ?? "",
    source: item.source,
    type: item.type,
    lastModified: item.lastModified,
    confidence: item.confidence,
    impact: item.impact ?? item.expectedImpact,
    effort: item.effort,
    sourceIssue: item.sourceIssue ?? "",
    whereToUpdate: Array.isArray(item.whereToUpdate) ? item.whereToUpdate : [],
    updateDetails: Array.isArray(item.updateDetails) ? item.updateDetails : [],
    references: Array.isArray(item.references) ? item.references : [],
  }));
  form.append("selectedQueueItems", JSON.stringify(cleanedItems));

  if (payload.file) form.append("file", payload.file);

  return apiFetch(`/sandbox/simulate`, { method: "POST", body: form as any });
}

export async function fetchSandboxHistory(params: { brand: string }): Promise<{ items: SimulationHistoryItem[] }> {
  return apiFetch(`/sandbox/history?brand=${encodeURIComponent(params.brand)}`);
}

export async function fetchSandboxRun(runId: number): Promise<SimulationRunDetails> {
  return apiFetch(`/sandbox/run/${runId}`);
}

/** A complete AI-proposed test: a buyer question + the queued actions to pair
 *  with it + a one-clause rationale. Item 0 is the recommendation. */
export type SandboxSuggestion = {
  question: string;
  actionIds: number[];
  /** Claude's query-specific estimate for THIS test (same rules as the sim),
   *  conservative; null when the model did not return one. */
  predictedBefore: number | null;
  predictedAfter: number | null;
  why: string;
};

/**
 * Claude-generated complete tests, grounded in the brand's monitoring
 * visibility and its queued recommended actions, best-first. Empty array means
 * "no suggestions" (UI hides the tiles + recommendation).
 */
export async function fetchSandboxSuggestions(payload: {
  brand: string;
  monitoringData?: MonitoringBeforeData | null;
  queueItems: SimulationQueueItem[];
}): Promise<{ suggestions: SandboxSuggestion[] }> {
  return apiFetch(`/sandbox/suggest-queries`, {
    method: "POST",
    body: JSON.stringify({
      brand: payload.brand,
      monitoringData: payload.monitoringData ? monitoringDataToWire(payload.monitoringData) : {},
      queueItems: (payload.queueItems || []).map((i) => ({
        id: i.id,
        title: i.title,
        type: i.type,
        summary: i.summary ?? i.description ?? "",
        description: i.description ?? i.summary ?? "",
        sourceIssue: i.sourceIssue ?? "",
        priority: i.priority,
        impact: i.impact ?? i.expectedImpact,
        effort: i.effort,
      })),
    }),
  });
}

export async function fetchAISandboxData(params: { brand: string }): Promise<AISandboxData> {
  const brand = (params.brand || "").trim();
  const history = brand ? await fetchSandboxHistory({ brand }) : { items: [] };

  // Queue from bm_last_result recommendedActions
  const bmRaw = safeJson(localStorage.getItem("bm_last_result") || "{}");
  const recommendedFromBm: any[] = Array.isArray(bmRaw?.recommendedActions) ? bmRaw.recommendedActions : [];
  let recommended: any[] = recommendedFromBm;

  if ((!recommended || recommended.length === 0) && history.items?.length) {
    try {
      const latestRun = await fetchSandboxRun(history.items[0].id);
      if (Array.isArray(latestRun?.selectedQueueItems) && latestRun.selectedQueueItems.length > 0) {
        recommended = latestRun.selectedQueueItems;
      }
    } catch (e) {
      console.warn("Unable to load latest sandbox run for queue fallback", e);
    }
  }

  const nowIso = new Date().toISOString();

  const simulationQueue: SimulationQueueItem[] = (recommended || [])
    .filter((a) => a && a.id !== undefined && a.id !== null)
    .map((a: any) => {
      const id = Number(a.id);
      const priority = String(a.priority ?? "MEDIUM");
      const impact = a.impact ?? a.expectedImpact ?? "";
      const effort = String(a.effort ?? "Medium");
      const summary = String(a.summary ?? a.description ?? "");
      return {
        id,
        title: String(a.title || "Untitled action"),
        type: String(a.type || "Recommended Action"),
        source: String(a.source || "Antenna"),
        confidence: Number(a.confidence ?? 80),
        expectedImpact: typeof impact === "number" ? `${impact}` : String(impact || ""),
        summary,
        lastModified: String(a.lastModified || nowIso),
        priority,
        description: String(a.description ?? summary),
        impact,
        effort,
        strategicRationale: {
          issues: Array.isArray(a?.strategicRationale?.issues) ? a.strategicRationale.issues : [],
          gaps: Array.isArray(a?.strategicRationale?.gaps) ? a.strategicRationale.gaps : [],
          opportunities: Array.isArray(a?.strategicRationale?.opportunities) ? a.strategicRationale.opportunities : [],
        },
        sourceIssue: typeof a?.sourceIssue === "string" ? a.sourceIssue : "",
        whereToUpdate: Array.isArray(a?.whereToUpdate) ? a.whereToUpdate : [],
        updateDetails: Array.isArray(a?.updateDetails) ? a.updateDetails : [],
        references: Array.isArray(a?.references)
          ? a.references
              .filter((r: any) => r && (r.label || r.excerpt))
              .map((r: any) => ({
                label: String(r.label || "Reference"),
                url: r.url ? String(r.url) : null,
                excerpt: String(r.excerpt || ""),
              }))
          : [],
      };
    });

  // Sandbox supports the same measurement models as the rest of the app.
  // Derived from the central catalog so adding a new live model surfaces here.
  const aiModels: AIModel[] = MEASUREMENT_MODELS.map((id) => ({
    id,
    name: getModelDisplayName(id),
    enabled: true,
    avgResponseTime: "—",
  }));

  return {
    simulationQueue,
    beforeAfterData: {
      before: { avgProbability: 0, topRank: 10, brandMentionRate: 0 },
      after:  { avgProbability: 0, topRank: 10, brandMentionRate: 0 },
      delta:  { probability: 0, rank: 0, mentionRate: 0 },
    },
    modelResults: [],
    aiModels,
    simulationHistory: history.items || [],
    comparisonData: [],
    timeSeriesData: [],
    responseQuality: [],
    discoveries: [],
    competitors: [],
    timeToImpact: [],
    rationale: { issues: [], gaps: [], opportunities: [] },
  };
}

function safeJson(txt: string): any {
  try { return JSON.parse(txt); } catch { return {}; }
}